import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Users, Briefcase } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import api, { formatDate, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function Rekrutmen() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [lembagas, setLembagas] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [open, setOpen] = useState(false);
  const [appOpen, setAppOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [appForm, setAppForm] = useState({});
  const canManage = ["superadmin","admin","hr"].includes(user?.role);

  const load = async () => {
    const [r, l] = await Promise.all([api.get("/rekrutmen"), api.get("/lembaga")]);
    setItems(r.data); setLembagas(l.data);
  };
  useEffect(() => { load(); }, []);

  const loadApplicants = async (jobId) => {
    setSelectedJob(items.find((i) => i.id === jobId));
    const { data } = await api.get(`/applicants?rekrutmen_id=${jobId}`);
    setApplicants(data);
  };

  const openNew = () => { setEditing(null); setForm({ title: "", lembaga_id: "", position: "", description: "", requirements: "", salary_range: "", deadline: "", status: "open" }); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...it, lembaga_id: it.lembaga_id || "" }); setOpen(true); };
  const save = async () => {
    try {
      const payload = { ...form, lembaga_id: form.lembaga_id || null };
      if (editing) await api.put(`/rekrutmen/${editing.id}`, payload);
      else await api.post("/rekrutmen", payload);
      toast.success("Lowongan tersimpan"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!confirm("Hapus lowongan?")) return; try { await api.delete(`/rekrutmen/${id}`); load(); toast.success("Terhapus"); } catch { toast.error("Gagal"); } };

  const openAddApplicant = () => { setAppForm({ rekrutmen_id: selectedJob.id, name: "", email: "", phone: "", education: "", experience: "", note: "" }); setAppOpen(true); };
  const saveApplicant = async () => {
    try { await api.post("/applicants", appForm); toast.success("Pelamar ditambahkan"); setAppOpen(false); loadApplicants(selectedJob.id); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const updateApplicantStatus = async (id, status) => {
    try { await api.put(`/applicants/${id}?status=${status}`); loadApplicants(selectedJob.id); } catch { toast.error("Gagal"); }
  };
  const removeApplicant = async (id) => {
    if (!confirm("Hapus pelamar?")) return;
    try { await api.delete(`/applicants/${id}`); loadApplicants(selectedJob.id); load(); } catch { toast.error("Gagal"); }
  };

  return (
    <div data-testid="rekrutmen-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#0B8D95] font-semibold mb-1">Human Resource</div>
          <h1 className="font-display text-3xl font-black text-gray-900">Rekrutmen</h1>
          <p className="text-gray-600 text-sm mt-1">Kelola lowongan dan pelamar untuk perekrutan karyawan baru.</p>
        </div>
        {canManage && <Button onClick={openNew} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="rekrutmen-add-button"><Plus size={16} className="mr-1.5" />Buka Lowongan</Button>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 space-y-3">
          {items.map((r) => {
            const lb = lembagas.find((l) => l.id === r.lembaga_id);
            const active = selectedJob?.id === r.id;
            return (
              <div key={r.id} onClick={() => loadApplicants(r.id)} className={`bg-white border rounded-md p-4 cursor-pointer card-lift ${active ? "border-[#0B8D95] ring-2 ring-[#0B8D95]/20" : "border-gray-200"}`} data-testid={`rekrutmen-item-${r.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="font-display font-bold text-gray-900">{r.title}</div>
                    <div className="text-xs text-gray-500">{r.position} · {lb?.name || "Yayasan"}</div>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${r.status === "open" ? "bg-[#CCFBF1] text-[#0B8D95]" : "bg-gray-100 text-gray-600"}`}>{r.status}</span>
                </div>
                <div className="text-xs text-gray-600 line-clamp-2">{r.description}</div>
                <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><Users size={12} />{r.applicants_count || 0} pelamar</span>
                  <span>Deadline: {r.deadline ? formatDate(r.deadline) : "-"}</span>
                </div>
                {canManage && (
                  <div className="flex gap-1 mt-2">
                    <button onClick={(e)=>{e.stopPropagation(); openEdit(r);}} className="p-1.5 hover:bg-gray-100 rounded text-gray-600" data-testid={`rekrutmen-edit-${r.id}`}><PencilSimple size={13} /></button>
                    <button onClick={(e)=>{e.stopPropagation(); remove(r.id);}} className="p-1.5 hover:bg-red-50 rounded text-red-600" data-testid={`rekrutmen-delete-${r.id}`}><Trash size={13} /></button>
                  </div>
                )}
              </div>
            );
          })}
          {items.length === 0 && <div className="bg-white border border-gray-200 rounded-md p-8 text-center text-gray-500 text-sm">Belum ada lowongan</div>}
        </div>

        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-md overflow-hidden">
          {selectedJob ? (
            <>
              <div className="p-5 border-b border-gray-200">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Detail Lowongan</div>
                    <div className="font-display text-xl font-bold text-gray-900 mt-1">{selectedJob.title}</div>
                    <div className="text-sm text-gray-600 mt-1">{selectedJob.position} · Deadline: {selectedJob.deadline ? formatDate(selectedJob.deadline) : "-"}</div>
                  </div>
                  {canManage && <Button size="sm" onClick={openAddApplicant} className="bg-[#0B8D95] hover:bg-[#48B8BD]" data-testid="applicant-add-button"><Plus size={14} className="mr-1" />Pelamar</Button>}
                </div>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div><div className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">Deskripsi</div><div className="text-gray-700">{selectedJob.description}</div></div>
                  <div><div className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">Persyaratan</div><div className="text-gray-700">{selectedJob.requirements}</div></div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
                    <tr><th className="px-4 py-3 font-semibold">Nama</th><th className="px-4 py-3 font-semibold">Kontak</th><th className="px-4 py-3 font-semibold">Pendidikan</th><th className="px-4 py-3 font-semibold">Status</th><th className="px-4 py-3 font-semibold text-right">Aksi</th></tr>
                  </thead>
                  <tbody>
                    {applicants.map((a) => (
                      <tr key={a.id} className="border-t border-gray-100">
                        <td className="px-4 py-2.5 font-semibold">{a.name}</td>
                        <td className="px-4 py-2.5 text-xs">
                          <div>{a.email}</div><div className="text-gray-500 font-mono">{a.phone}</div>
                        </td>
                        <td className="px-4 py-2.5 text-xs">{a.education}</td>
                        <td className="px-4 py-2.5">
                          <Select value={a.status} onValueChange={(v)=>updateApplicantStatus(a.id, v)}>
                            <SelectTrigger className="w-32 bg-white h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-white">
                              <SelectItem value="new">Baru</SelectItem><SelectItem value="screening">Screening</SelectItem>
                              <SelectItem value="interview">Interview</SelectItem><SelectItem value="offered">Ditawari</SelectItem>
                              <SelectItem value="hired">Diterima</SelectItem><SelectItem value="rejected">Ditolak</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <button onClick={()=>removeApplicant(a.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600"><Trash size={14} /></button>
                        </td>
                      </tr>
                    ))}
                    {applicants.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-gray-500 text-sm">Belum ada pelamar</td></tr>}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="p-12 text-center text-gray-500 flex flex-col items-center gap-3">
              <Briefcase size={48} className="text-gray-300" weight="duotone" />
              <div>Pilih lowongan untuk melihat detail dan pelamar</div>
            </div>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white sm:max-w-lg" data-testid="rekrutmen-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Lowongan" : "Buka Lowongan"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Judul Lowongan *</Label><Input value={form.title || ""} onChange={(e)=>setForm({...form, title: e.target.value})} data-testid="rekrutmen-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Jabatan *</Label><Input value={form.position || ""} onChange={(e)=>setForm({...form, position: e.target.value})} /></div>
              <div><Label>Lembaga</Label>
                <Select value={form.lembaga_id || "none"} onValueChange={(v)=>setForm({...form, lembaga_id: v === "none" ? "" : v})}>
                  <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="none">Umum</SelectItem>{lembagas.map((l)=><SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Deskripsi *</Label><Textarea value={form.description || ""} onChange={(e)=>setForm({...form, description: e.target.value})} /></div>
            <div><Label>Persyaratan *</Label><Textarea value={form.requirements || ""} onChange={(e)=>setForm({...form, requirements: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Range Gaji</Label><Input value={form.salary_range || ""} onChange={(e)=>setForm({...form, salary_range: e.target.value})} placeholder="Rp 3jt - 5jt" /></div>
              <div><Label>Deadline</Label><Input type="date" value={form.deadline || ""} onChange={(e)=>setForm({...form, deadline: e.target.value})} /></div>
            </div>
            <div><Label>Status</Label>
              <Select value={form.status || "open"} onValueChange={(v)=>setForm({...form, status: v})}>
                <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="open">Open</SelectItem><SelectItem value="closed">Closed</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button>
            <Button onClick={save} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="rekrutmen-save-button">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={appOpen} onOpenChange={setAppOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Tambah Pelamar</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nama *</Label><Input value={appForm.name || ""} onChange={(e)=>setAppForm({...appForm, name: e.target.value})} data-testid="applicant-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email *</Label><Input value={appForm.email || ""} onChange={(e)=>setAppForm({...appForm, email: e.target.value})} /></div>
              <div><Label>No. HP *</Label><Input value={appForm.phone || ""} onChange={(e)=>setAppForm({...appForm, phone: e.target.value})} /></div>
            </div>
            <div><Label>Pendidikan *</Label><Input value={appForm.education || ""} onChange={(e)=>setAppForm({...appForm, education: e.target.value})} placeholder="S1 Pendidikan..." /></div>
            <div><Label>Pengalaman</Label><Textarea value={appForm.experience || ""} onChange={(e)=>setAppForm({...appForm, experience: e.target.value})} /></div>
            <div><Label>Catatan</Label><Input value={appForm.note || ""} onChange={(e)=>setAppForm({...appForm, note: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setAppOpen(false)}>Batal</Button>
            <Button onClick={saveApplicant} className="bg-[#0B8D95] hover:bg-[#48B8BD] text-white" data-testid="applicant-save-button">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
